import { useCallback, useState } from "react";
import BaseButton from "../common/baseButton";
import ArrowElement from "@/assets/images/ArrowElement.svg";
import { useTranslation } from "react-i18next";
import { Modal } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { checkPermissionsWithoutAlert } from "@/util";
import { PermissionName } from "@/models/apiModels";
import { EmployeeFilterResource } from "@/models/employees";
import { EmployeeActionType } from "@/models/enums";
import popupService from "@/services/popupService";
import api from "@/services/api";
import BaseTooltip from "../common/baseTooltip";

interface EmployeesActionButtonProps {
    checkAll: boolean;
    selectedEmployees: number[];
    excludedEmployees: number[];
    handleClearFilters: Function;
    filterRequest: EmployeeFilterResource;
}

const EmployeesActionButton = ({
    checkAll,
    selectedEmployees,
    handleClearFilters,
    filterRequest,
    excludedEmployees,
}: EmployeesActionButtonProps) => {
    const { t } = useTranslation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showActionChoose, setShowActionChoose] = useState(false);
    const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState(false);

    const handleMakeAction = async (actionType: EmployeeActionType) => {
        popupService.showLoading();

        try {
            const response = await api.employeeActions({
                filterRequest,
                actionType,
                checkAll,
                selectedEmployees,
                excludedEmployees,
            });

            await popupService.showSuccess(
                t("actions.successEmployeeUpdate").replace("{0}", response.data.updatedCount.toString())
            );

            setOpenUpdateStatusModal(false);
            setShowDropdown(false);
            handleClearFilters();
        } catch (err) {
            popupService.showApiError(err);
        }
    };

    const handleUpdateStatus = useCallback(() => {
        setOpenUpdateStatusModal(true);
        setShowActionChoose(false);
    }, []);

    const updateStatusModal = openUpdateStatusModal && (
        <div>
            <Modal
                open={openUpdateStatusModal}
                onClose={() => setOpenUpdateStatusModal(false)}
                sx={{ backdropFilter: "blur(6px)" }}
            >
                <div className="new-forms-modal-style">
                    <div className="new-forms-modal-header">
                        <CancelIcon
                            onClick={() => setOpenUpdateStatusModal(false)}
                            style={{ color: "#586A84", cursor: "pointer" }}
                        />
                    </div>
                    <div className="new-forms-modal-content employee-import-export-modal-content">
                        <div
                            onClick={() => handleMakeAction(EmployeeActionType.SetStatusActive)}
                            className="new-forms-modal-button"
                        >
                            <div className="new-forms-modal-button-content">{t("actions.setStatusActive")}</div>
                        </div>
                        <div
                            onClick={() => handleMakeAction(EmployeeActionType.SetStatusInactive)}
                            className="new-forms-modal-button"
                        >
                            <div className="new-forms-modal-button-content">{t("actions.setStatusInactive")}</div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );

    return (
        <div className="employees-action-button-block">
            <BaseTooltip
                disableHoverListener={checkAll === true || selectedEmployees.length > 0}
                title={t("actions.selectEmployeeForActions")}
            >
                <BaseButton
                    className="basic-button employee-action-btn"
                    disabled={checkAll === false && selectedEmployees.length === 0}
                    onClick={() => setShowDropdown((prevValue) => !prevValue)}
                >
                    <div>{t("general.action")}</div>
                    <img src={ArrowElement} alt="ArrowElement" />
                </BaseButton>
            </BaseTooltip>

            {showDropdown && (
                <div className="employees-action-dropdown">
                    <div
                        className="employees-action-select-btn"
                        onClick={() => setShowActionChoose((prevValue) => !prevValue)}
                        aria-disabled={checkPermissionsWithoutAlert(PermissionName.EditEmployee)}
                    >
                        {t("actions.updateSelected")}
                    </div>
                    {showActionChoose && (
                        <div className="update-selected-actions-choose">
                            <div className="employees-action-select-btn" onClick={() => handleUpdateStatus()}>
                                {t("actions.setEmployeeStatus")}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {updateStatusModal}
        </div>
    );
};

export default EmployeesActionButton;
